import axiosConfig from "./axiosConfig";

class ArticleService {
  constructor() {
    this.baseUrl = "/v1/articles";
    this.defaultLimit = 50;
  }

  /**
   * Normalise la gestion d'erreur
   * @param {Error} error L'erreur à traiter
   * @param {string} errorMessage Message d'erreur personnalisé
   * @returns {Object} Données par défaut en cas d'erreur
   */
  _handleError(
    error,
    errorMessage = "Error fetching articles:",
    defaultReturn = { data: [] }
  ) {
    console.error(errorMessage, error);
    if (error.response && error.response.data) {
      return Promise.reject(error.response.data);
    }
    return defaultReturn;
  }

  /**
   * Récupère tous les articles
   * @param {string} data Paramètres additionnels
   * @param {number} limit Nombre maximum d'articles à récupérer
   * @returns {Promise<Object>} La réponse de l'API
   */
  async getAll(data = "", limit = this.defaultLimit) {
    try {
      const response = await axiosConfig.get(
        `${this.baseUrl}/${data}?limit=${limit}&order_by=created_at&order=asc`
      );
      return response.data || { data: [] };
    } catch (error) {
      return this._handleError(error, "Error fetching articles:");
    }
  }

  /**
   * Récupère tous les articles par statut
   * @param {number} status_id ID du statut
   * @param {number} limit Nombre maximum d'articles
   * @param {number} page Numéro de page
   * @returns {Promise<Object>} La réponse de l'API
   */
  async getAllByStatus(status_id = 1, limit = 500, page = 1) {
    try {
      const response = await axiosConfig.get(
        `${this.baseUrl}/status/${status_id}?limit=${limit}&page=${page}`
      );
      return response.data || { data: [] };
    } catch (error) {
      return this._handleError(error, "Error fetching articles by status:");
    }
  }

  /**
   * Récupère tous les articles partagés
   * @param {number} limit Nombre maximum d'articles
   * @returns {Promise<Object>} La réponse de l'API
   */
  async getAllPartage(limit = this.defaultLimit) {
    try {
      const response = await axiosConfig.get(
        `${this.baseUrl}/partager-au-comite?limit=${limit}&page=1`
      );
      return response.data || { data: [] };
    } catch (error) {
      return this._handleError(error, "Error fetching shared articles:");
    }
  }

  /**
   * Récupère tous les articles publiés
   * @param {number} limit Nombre maximum d'articles
   * @returns {Promise<Object>} La réponse de l'API
   */
  async getAllArticlePublie(limit = this.defaultLimit) {
    try {
      const response = await axiosConfig.get(
        `${this.baseUrl}/publier?limit=${limit}&page=1&order_by=date_pub_art&order=desc`
      );
      return response.data || { data: [] };
    } catch (error) {
      return this._handleError(error, "Error fetching published articles:");
    }
  }

  /**
   * Publie un article
   * @param {number} articleId ID de l'article à publier
   * @param {FormData} formData Données du formulaire
   * @returns {Promise<Object>} La réponse de l'API
   */
  async publishArticle(articleId, formData) {
    try {
      const response = await axiosConfig.post(
        `${this.baseUrl}/${articleId}/publish`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la publication des articles:", error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Met à jour un article
   * @param {number} articleId ID de l'article à mettre à jour
   * @param {FormData} submitData Données du formulaire
   * @returns {Promise<Object>} La réponse de l'API
   */
  update(id, formData) {
    return axiosConfig.post(`${this.baseUrl}/${id}?_method=PUT`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(response => {
      this.invalidateCache?.();
      return response.data;
    })
    .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Publie plusieurs articles en parallèle
   * @param {Array} articleDataArray Tableau d'objets {articleId, formData}
   * @returns {Promise<Array>} Tableau des réponses
   */
  async publishMultipleArticles(articleDataArray) {
    const promises = articleDataArray.map(({ articleId, formData }) =>
      this.publishArticle(articleId, formData)
    );
    return await Promise.all(promises);
  }

  /**
   * Met à jour plusieurs articles en parallèle
   * @param {Array} updateDataArray Tableau d'objets {articleId, submitData}
   * @returns {Promise<Array>} Tableau des réponses
   */
  async updateMultipleArticles(updateDataArray) {
    const promises = updateDataArray.map(({ articleId, submitData }) =>
      this.update(articleId, submitData)
    );
    return await Promise.all(promises);
  }

  /**
   * Récupère un article par son ID
   * @param {number} id ID de l'article
   * @returns {Promise<Object>} La réponse de l'API
   */
  async getAllById(id) {
    try {
      const response = await axiosConfig.get(`${this.baseUrl}/id/${id}`);
      return response.data;
    } catch (error) {
      return this._handleError(error, "Error fetching article by ID:");
    }
  }

  /**
   * Récupère un article par son slug
   * @param {string} slug Slug de l'article
   * @returns {Promise<Object>} La réponse de l'API
   */
  async getAllBySlug(slug) {
    try {
      const response = await axiosConfig.get(`${this.baseUrl}/${slug}`);
      return response.data;
    } catch (error) {
      return this._handleError(error, "Error fetching article by slug:");
    }
  }
}

const ArticleServiceInstance = new ArticleService();
export default ArticleServiceInstance;